import { GameState } from '../game/GameState.js';
import type { Color, Move, Square } from '../game/types.js';
import { drawBoard } from '../render/BoardRenderer.js';
import { squareToCanvasCenter, type ViewerRotation } from '../render/geometry.js';
import { drawPiece, drawPieces } from '../render/PieceRenderer.js';
import {
  frameAt,
  planMoveAnimation,
  type MoveAnimationFrame,
  type MoveAnimationPlan,
} from '../render/PieceAnimator.js';
import {
  drawTurnBanner,
  type TurnBannerView,
} from '../render/OverlayRenderer.js';
import type { RenderPalette } from '../render/palette.js';
import { ReplayControls } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { ReplayController, type ReplayMoveEvent } from './ReplayController.js';
import type { GameRecord, RecordedMove } from './types.js';

export type ReplayReturnTarget = 'history' | 'menu';

interface ReplaySceneDeps {
  readonly requestRender: () => void;
  readonly onExit: (target: ReplayReturnTarget) => void;
  readonly getRotation: () => ViewerRotation;
}

interface ActiveAnimation {
  readonly plan: MoveAnimationPlan;
  readonly preState: GameState;
  readonly move: Move;
  readonly mover: Color;
  readonly startMs: number;
}

const AUTO_PLAY_DELAY_MS = 380;

export class ReplayScene {
  private readonly deps: ReplaySceneDeps;
  private controller: ReplayController | null = null;
  private controls: ReplayControls | null = null;
  private returnTo: ReplayReturnTarget = 'menu';
  private isPlaying = false;
  private animation: ActiveAnimation | null = null;
  private playTimer: ReturnType<typeof setTimeout> | null = null;
  private speed = 1;

  constructor(deps: ReplaySceneDeps) {
    this.deps = deps;
  }

  get isOpen(): boolean {
    return this.controller !== null;
  }

  open(record: GameRecord, returnTo: ReplayReturnTarget): void {
    if (this.controller) this.close();

    this.returnTo = returnTo;
    this.speed = 1;
    this.isPlaying = false;
    this.animation = null;

    const controller = new ReplayController(record);
    controller.setListener(() => {
      this.updateControlsState();
      this.deps.requestRender();
    });
    this.controller = controller;

    this.controls = new ReplayControls(
      {
        onFirst: () => this.seek(0),
        onPrev: () => this.seek(controller.getStepIndex() - 1),
        onPlayPause: () => this.togglePlay(),
        onNext: () => this.manualNext(),
        onLast: () => this.seek(controller.getTotalSteps()),
        onSpeed: (s) => this.setSpeed(s),
        onBack: () => this.exit(),
      },
      this.controlsState(),
    );
    this.controls.mount();
  }

  close(): void {
    if (this.playTimer !== null) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }
    this.controls?.unmount();
    this.controls = null;
    this.controller = null;
    this.isPlaying = false;
    this.animation = null;
  }

  exit(): void {
    const target = this.returnTo;
    this.close();
    this.deps.onExit(target);
  }

  /** Draw the scene onto the host canvas. Called each frame from main.ts'
   * render loop while replay is the active scene. */
  render(ctx: CanvasRenderingContext2D, palette: RenderPalette, timeMs: number): void {
    if (!this.controller) return;
    const rotation = this.deps.getRotation();
    drawBoard(ctx, palette, rotation);

    let animFrame: MoveAnimationFrame | null = null;
    if (this.animation) {
      const elapsed = (timeMs - this.animation.startMs) * this.speed;
      animFrame = frameAt(this.animation.plan, elapsed, rotation);
      if (animFrame.done) {
        this.finishAnimation();
        animFrame = null;
      }
    }

    const displayState = this.animation
      ? this.animation.preState
      : this.controller.getState();

    const skip = new Set<string>();
    if (this.animation) {
      skip.add(squareKey(this.animation.move.from));
      if (animFrame) {
        for (const cap of animFrame.captures) {
          if (cap.alpha > 0) skip.add(squareKey(cap.square));
        }
      }
    }

    drawPieces(ctx, displayState, palette, rotation, (sq) => skip.has(squareKey(sq)));

    if (this.animation && animFrame) {
      for (const cap of animFrame.captures) {
        if (cap.alpha <= 0) continue;
        const piece = this.animation.preState.pieceAt(cap.square);
        if (!piece) continue;
        const { x, y } = squareToCanvasCenter(cap.square, rotation);
        drawPiece(ctx, x, y, piece.color, piece.kind, palette, { alpha: cap.alpha });
      }
      drawPiece(
        ctx,
        animFrame.mover.x,
        animFrame.mover.y,
        this.animation.mover,
        animFrame.mover.kind,
        palette,
        {
          scale: animFrame.mover.scale,
          shadowLift: this.animation.move.captures.length > 0 ? 4 : 0,
        },
      );
      if (animFrame.promotionFlash > 0) {
        this.drawPromotionFlash(ctx, animFrame, this.animation.mover, palette);
      }
    }

    drawTurnBanner(ctx, this.bannerView(), palette);
  }

  private bannerView(): TurnBannerView {
    if (!this.controller) return { kind: 'turn', player: 'white' };
    if (this.controller.isAtEnd()) {
      const record = this.controller.getRecord();
      if (record.result === 'draw') return { kind: 'draw' };
      if (record.winner !== null) return { kind: 'win', winner: record.winner };
    }
    return { kind: 'turn', player: this.controller.getState().currentPlayer };
  }

  private drawPromotionFlash(
    ctx: CanvasRenderingContext2D,
    animFrame: MoveAnimationFrame,
    color: Color,
    palette: RenderPalette,
  ): void {
    const alpha = animFrame.promotionFlash;
    if (alpha <= 0) return;
    ctx.save();
    ctx.beginPath();
    ctx.arc(animFrame.mover.x, animFrame.mover.y, 32, 0, Math.PI * 2);
    ctx.strokeStyle = color === 'white' ? palette.whiteCrownGold : palette.blackCrownGold;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 3;
    ctx.shadowColor = ctx.strokeStyle;
    ctx.shadowBlur = 14;
    ctx.stroke();
    ctx.restore();
  }

  private controlsState() {
    const c = this.controller;
    return {
      stepIndex: c ? c.getStepIndex() : 0,
      totalSteps: c ? c.getTotalSteps() : 0,
      speed: this.speed,
      isPlaying: this.isPlaying,
      backLabel: this.returnTo === 'history'
        ? t('replay.controls.backToHistory')
        : t('replay.controls.backToMenu'),
    };
  }

  private updateControlsState(): void {
    this.controls?.update(this.controlsState());
  }

  private setSpeed(next: number): void {
    if (next === this.speed) return;
    // Rebase startMs so the in-flight animation continues from the same frame
    // — preserves visual continuity instead of snapping forward or backward.
    if (this.animation) {
      const now = performance.now();
      const elapsedAtOldSpeed = (now - this.animation.startMs) * this.speed;
      const rebasedStart = now - elapsedAtOldSpeed / next;
      this.animation = { ...this.animation, startMs: rebasedStart };
    }
    this.speed = next;
    this.updateControlsState();
  }

  private seek(target: number): void {
    if (!this.controller) return;
    this.cancelAnimation();
    this.controller.seek(target);
    this.updateControlsState();
    this.deps.requestRender();
  }

  private cancelAnimation(): void {
    this.animation = null;
    if (this.playTimer !== null) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }
  }

  private manualNext(): void {
    if (!this.controller) return;
    if (this.animation) {
      // Snap-finish the in-flight animation, then start the next.
      this.cancelAnimation();
      this.deps.requestRender();
    }
    this.stepWithAnimation(false);
  }

  private togglePlay(): void {
    if (!this.controller) return;
    if (this.isPlaying) {
      this.isPlaying = false;
      if (this.playTimer !== null) {
        clearTimeout(this.playTimer);
        this.playTimer = null;
      }
      this.updateControlsState();
      return;
    }
    if (this.controller.isAtEnd()) return;
    this.isPlaying = true;
    this.updateControlsState();
    this.stepWithAnimation(true);
  }

  private stepWithAnimation(autoplaying: boolean): void {
    if (!this.controller) return;
    if (this.controller.isAtEnd()) {
      this.isPlaying = false;
      this.updateControlsState();
      return;
    }
    const event = this.controller.stepForward();
    if (!event) {
      this.isPlaying = false;
      this.updateControlsState();
      return;
    }
    this.startAnimation(event, autoplaying);
  }

  private startAnimation(event: ReplayMoveEvent, autoplaying: boolean): void {
    const mover = event.preState.pieceAt(event.move.from);
    if (!mover) {
      // Defensive — should never happen for a valid record.
      if (autoplaying && this.isPlaying) this.scheduleNext();
      return;
    }
    const move = toMove(event.move);
    const plan = planMoveAnimation(move, mover);
    this.animation = {
      plan,
      preState: event.preState,
      move,
      mover: mover.color,
      startMs: performance.now(),
    };
    this.deps.requestRender();
  }

  private finishAnimation(): void {
    this.animation = null;
    this.deps.requestRender();
    if (this.isPlaying) this.scheduleNext();
    this.updateControlsState();
  }

  private scheduleNext(): void {
    if (this.playTimer !== null) clearTimeout(this.playTimer);
    this.playTimer = setTimeout(() => {
      this.playTimer = null;
      if (this.isPlaying && this.controller && !this.controller.isAtEnd()) {
        this.stepWithAnimation(true);
      } else {
        this.isPlaying = false;
        this.updateControlsState();
      }
    }, Math.max(80, AUTO_PLAY_DELAY_MS / this.speed));
  }
}

function squareKey(sq: Square): string {
  return `${sq.row},${sq.col}`;
}

function toMove(recorded: RecordedMove): Move {
  return {
    from: recorded.from,
    to: recorded.to,
    path: recorded.path,
    captures: recorded.captures,
    promoted: recorded.promoted,
  };
}
